import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Image, Plus, RefreshCw, Trash2, Trophy, Upload, Users, ZoomIn, CircleDot, Swords, Target } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { MediaPickerDialog } from './AdminShared';

// Reliable sport icon mapping (emojis render as text on some devices)
const SPORT_ICONS = {
  'soccer': CircleDot,
  'rugby': Swords,
  'gaelic-football': Target,
  'hurling-camogie': Swords,
};

const SportIcon = ({ sport, className = "w-5 h-5" }) => {
  const IconComponent = SPORT_ICONS[sport?.slug] || Trophy;
  return <IconComponent className={className} />;
};

const AdminPartners = ({ partners, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    website: '',
    category: 'partner',
    color: 'sky',
    order: 0,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const colorOptions = [
    { value: 'sky', label: 'Sky Blue', class: 'bg-sky-500' },
    { value: 'green', label: 'Green', class: 'bg-green-500' },
    { value: 'red', label: 'Red', class: 'bg-red-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-500' },
    { value: 'pink', label: 'Pink', class: 'bg-pink-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
  ];

  const categoryOptions = [
    { value: 'partner', label: 'Partner' },
    { value: 'affiliate', label: 'Affiliate' },
    { value: 'sponsor', label: 'Sponsor' },
    { value: 'league', label: 'League Partner' },
  ];

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, logo: res.data.url });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '',
      website: '',
      category: 'partner',
      color: 'sky',
      order: 0,
      is_active: true
    });
    setEditingPartner(null);
    setShowForm(false);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description,
      logo: partner.logo || '',
      website: partner.website || '',
      category: partner.category || 'partner',
      color: partner.color || 'sky',
      order: partner.order || 0,
      is_active: partner.is_active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await api.updatePartner(editingPartner.id, formData);
      } else {
        await api.createPartner(formData);
      }
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await api.deletePartner(id);
      onRefresh();
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Failed to delete partner');
    }
  };

  const getColorClass = (color, opacity = '500') => {
    const colors = {
      sky: `bg-sky-${opacity}`,
      green: `bg-green-${opacity}`,
      red: `bg-red-${opacity}`,
      purple: `bg-purple-${opacity}`,
      yellow: `bg-yellow-${opacity}`,
      pink: `bg-pink-${opacity}`,
      orange: `bg-orange-${opacity}`,
    };
    return colors[color] || colors.sky;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Partners & Affiliations</h2>
        <Button 
          onClick={() => setShowForm(true)} 
          className="bg-sky-400 hover:bg-sky-500"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Partner
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingPartner ? 'Edit Partner' : 'Add New Partner'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Partner Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., New York GAA"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Website URL</Label>
                  <Input 
                    value={formData.website} 
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Description *</Label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 min-h-[80px]"
                  placeholder="Brief description of the partnership..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Category</Label>
                  <select 
                    value={formData.category} 
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
                  >
                    {categoryOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Display Color</Label>
                  <select 
                    value={formData.color} 
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
                  >
                    {colorOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Display Order</Label>
                  <Input 
                    type="number" 
                    value={formData.order} 
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Logo</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.logo} 
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="URL or upload/browse"
                  />
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    className="border-gray-600"
                    onClick={() => setShowMediaPicker(true)}
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".png,.svg,.webp,.jpg,.jpeg,image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                    </span>
                  </label>
                  {formData.logo && (
                    <img 
                      src={resolveMediaUrl(formData.logo)} 
                      alt="Logo preview" 
                      className="h-12 w-12 object-contain bg-gray-700 rounded p-1"
                    />
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                  id="is_active"
                />
                <Label htmlFor="is_active" className="text-gray-300">Active (visible on About Us page)</Label>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400 text-gray-900">
                  {editingPartner ? 'Update Partner' : 'Add Partner'}
                </Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Partners List */}
      <div className="grid gap-4">
        {partners.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">No partners added yet. Click &quot;Add Partner&quot; to create your first one.</p>
            </CardContent>
          </Card>
        ) : (
          partners.map(partner => (
            <Card key={partner.id} className={`bg-gray-800/50 border-gray-700 ${!partner.is_active ? 'opacity-50' : ''}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {partner.logo ? (
                    <div className={`w-14 h-14 rounded-lg ${getColorClass(partner.color, '500')}/20 p-2 flex items-center justify-center`}>
                      <img src={resolveMediaUrl(partner.logo)} alt={partner.name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-lg ${getColorClass(partner.color, '500')}/20 flex items-center justify-center`}>
                      <Trophy className={`w-7 h-7 text-${partner.color}-400`} />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold">{partner.name}</h3>
                      {!partner.is_active && (
                        <span className="px-2 py-0.5 bg-gray-600 text-gray-300 rounded text-xs">Inactive</span>
                      )}
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 rounded text-xs capitalize">{partner.category}</span>
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-1">{partner.description}</p>
                    {partner.website && (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline">
                        {partner.website}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(partner)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(partner.id)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Admin Page Banners Management
const AdminPageBanners = ({ onRefresh }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    hero_image: '',
    hero_title: '',
    hero_subtitle: '',
    overlay_opacity: 0.5,
    image_position: 'center',
    image_zoom: 100,
    image_position_x: 50,
    image_position_y: 50,
    is_active: true
  });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultPages = [
    { page_slug: 'home', page_name: 'Home' },
    { page_slug: 'about', page_name: 'About Us' },
    { page_slug: 'scores', page_name: 'Live Scores' },
    { page_slug: 'fixtures', page_name: 'Fixtures' },
    { page_slug: 'sport-soccer', page_name: 'Soccer' },
    { page_slug: 'sport-gaelic-football', page_name: 'Gaelic Football' },
    { page_slug: 'sport-hurling-camogie', page_name: 'Hurling/Camogie' },
    { page_slug: 'sport-rugby', page_name: 'Rugby' },
  ];

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      // First init default banners
      await api.initPageBanners();
      const res = await api.getPageBanners();
      setBanners(res.data);
    } catch (error) {
      console.error('Error loading page banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      hero_image: banner.hero_image || '',
      hero_title: banner.hero_title || '',
      hero_subtitle: banner.hero_subtitle || '',
      overlay_opacity: banner.overlay_opacity || 0.5,
      image_position: banner.image_position || 'center',
      image_zoom: banner.image_zoom || 100,
      image_position_x: banner.image_position_x ?? 50,
      image_position_y: banner.image_position_y ?? 50,
      is_active: banner.is_active !== false
    });
  };

  const handleSave = async () => {
    if (!editingBanner) return;
    setSaving(true);
    try {
      await api.updatePageBanner(editingBanner.page_slug, formData);
      await loadBanners();
      setEditingBanner(null);
      setFormData({ hero_image: '', hero_title: '', hero_subtitle: '', overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true });
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingBanner(null);
    setFormData({ hero_image: '', hero_title: '', hero_subtitle: '', overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true });
  };

  const resetImageTransform = () => {
    setFormData({ ...formData, image_zoom: 100, image_position_x: 50, image_position_y: 50 });
  };

  // Merge default pages with existing banners
  const allPages = defaultPages.map(page => {
    const existing = banners.find(b => b.page_slug === page.page_slug);
    return existing || { ...page, hero_image: null, hero_title: null, hero_subtitle: null, overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true };
  });

  if (loading) {
    return <div className="text-gray-400 text-center py-8">Loading page banners...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Page Banners</h2>
          <p className="text-gray-400 text-sm mt-1">Set hero images for each page of your site</p>
        </div>
      </div>

      {/* Edit Form */}
      {editingBanner && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">Edit Banner: {editingBanner.page_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">Hero Image</Label>
              <div className="flex gap-2">
                <Input 
                  value={formData.hero_image} 
                  onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white flex-1"
                  placeholder="Image URL or browse from Media Library"
                />
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  className="border-gray-600"
                  onClick={() => setShowMediaPicker(true)}
                >
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {formData.hero_image && (
                <div className="mt-2 relative rounded-lg overflow-hidden" style={{ height: '120px' }}>
                  <img 
                    src={resolveMediaUrl(formData.hero_image)} 
                    alt="Preview" 
                    className="w-full h-full"
                    style={{
                      objectFit: 'cover',
                      objectPosition: `${formData.image_position_x}% ${formData.image_position_y}%`,
                      transform: `scale(${formData.image_zoom / 100})`,
                      transformOrigin: `${formData.image_position_x}% ${formData.image_position_y}%`
                    }}
                  />
                  <div className="absolute inset-0 bg-black" style={{ opacity: formData.overlay_opacity }} />
                  {formData.hero_title && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{formData.hero_title}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Title Overlay (optional)</Label>
                <Input 
                  value={formData.hero_title} 
                  onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="e.g., About Us"
                />
              </div>
              <div>
                <Label className="text-gray-300">Subtitle (optional)</Label>
                <Input 
                  value={formData.hero_subtitle} 
                  onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="e.g., Learn about our services"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Overlay Darkness: {Math.round(formData.overlay_opacity * 100)}%</Label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1"
                value={formData.overlay_opacity}
                onChange={(e) => setFormData({ ...formData, overlay_opacity: parseFloat(e.target.value) })}
                className="w-full mt-2"
              />
            </div>

            <div>
              <Label className="text-gray-300">Image Vertical Alignment</Label>
              <div className="flex gap-2 mt-2">
                {['top', 'center', 'bottom'].map(pos => (
                  <Button
                    key={pos}
                    type="button"
                    variant={formData.image_position === pos ? "default" : "outline"}
                    className={formData.image_position === pos ? "bg-sky-500" : "border-gray-600 text-gray-300"}
                    onClick={() => setFormData({ ...formData, image_position: pos })}
                  >
                    {pos.charAt(0).toUpperCase() + pos.slice(1)}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Controls which part of the image is shown when cropped</p>
            </div>

            {/* Zoom & Pan Controls */}
            {formData.hero_image && (
              <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <ZoomIn className="w-4 h-4 text-sky-400" />
                    Image Zoom & Position
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetImageTransform}
                    className="text-gray-400 hover:text-white text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Zoom: {formData.image_zoom}%</Label>
                    <input
                      type="range"
                      min="100"
                      max="200"
                      value={formData.image_zoom}
                      onChange={(e) => setFormData({ ...formData, image_zoom: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Horizontal: {formData.image_position_x}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.image_position_x}
                      onChange={(e) => setFormData({ ...formData, image_position_x: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Vertical: {formData.image_position_y}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.image_position_y}
                      onChange={(e) => setFormData({ ...formData, image_position_y: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                </div>
                <p className="text-gray-500 text-xs">Adjust zoom and position to frame your hero image perfectly.</p>
              </div>
            )}

            <MediaPickerDialog
              open={showMediaPicker}
              onOpenChange={setShowMediaPicker}
              onSelect={(url) => setFormData({ ...formData, hero_image: url })}
            />

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="bg-sky-300 hover:bg-sky-400 text-gray-900">
                {saving ? 'Saving...' : 'Save Banner'}
              </Button>
              <Button type="button" variant="outline" className="border-gray-600" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pages List */}
      <div className="grid gap-4 md:grid-cols-2">
        {allPages.map(page => (
          <Card 
            key={page.page_slug} 
            className={`bg-gray-800/50 border-gray-700 cursor-pointer hover:border-sky-500/50 transition-colors ${editingBanner?.page_slug === page.page_slug ? 'border-sky-500' : ''}`}
            onClick={() => handleEdit(page)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Thumbnail Preview */}
                <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                  {page.hero_image ? (
                    <img 
                      src={resolveMediaUrl(page.hero_image)} 
                      alt={page.page_name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <Image className="w-6 h-6" />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1">
                  <h3 className="text-white font-semibold">{page.page_name}</h3>
                  <p className="text-sm text-gray-400">/{page.page_slug.replace('sport-', '').replace('about', 'about')}</p>
                  {page.hero_image ? (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                      Image Set
                    </span>
                  ) : (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-gray-600 text-gray-400 rounded text-xs">
                      No Image
                    </span>
                  )}
                </div>
                <Edit className="w-4 h-4 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Admin Leagues Management
const AdminLeagues = ({ leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [formData, setFormData] = useState({ sport_id: '', name: '', slug: '', logo: '', order: 0 });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, SVG, or WebP file for transparent logos');
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', `${formData.name || 'League'} logo`);
      uploadFormData.append('tags', 'logo,league');
      
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const logoUrl = resolveMediaUrl(res.data.url);
      setFormData({ ...formData, logo: logoUrl });
    } catch (error) {
      alert('Error uploading logo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLeague) {
        await api.updateLeague(editingLeague.id, formData);
      } else {
        await api.createLeague(formData);
      }
      resetForm();
      onRefresh();
    } catch (error) {
      alert('Error saving league');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingLeague(null);
    setFormData({ sport_id: '', name: '', slug: '', logo: '', order: 0 });
  };

  const handleEdit = (league) => {
    setEditingLeague(league);
    setFormData({
      sport_id: league.sport_id,
      name: league.name,
      slug: league.slug,
      logo: league.logo || '',
      order: league.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this league? All teams in this league will need to be reassigned.')) {
      try {
        await api.deleteLeague(id);
        onRefresh();
      } catch (error) {
        alert('Error deleting league');
      }
    }
  };

  const getSportName = (sportId) => sports.find(s => s.id === sportId)?.name || 'Unknown';
  const getSportIcon = (sportId) => sports.find(s => s.id === sportId)?.icon || '🏆';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Leagues & Competitions ({leagues.length})</h2>
        <Button onClick={() => { setShowForm(true); setEditingLeague(null); setFormData({ sport_id: '', name: '', slug: '', logo: '', order: 0 }); }} className="bg-sky-300 hover:bg-sky-400 text-gray-900">
          <Plus className="w-4 h-4 mr-2" /> Add League
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingLeague ? 'Edit League' : 'Add New League'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Sport</Label>
                  <Select value={formData.sport_id} onValueChange={(v) => setFormData({ ...formData, sport_id: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select Sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {sports.map(sport => (
                        <SelectItem key={sport.id} value={sport.id} className="text-white hover:bg-gray-700">
                          <span className="flex items-center gap-1.5"><SportIcon sport={sport} className="w-3.5 h-3.5 inline" /> {sport.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">League Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Premier League"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Slug</Label>
                  <Input 
                    value={formData.slug} 
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="premier-league"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Display Order</Label>
                  <Input 
                    type="number"
                    value={formData.order} 
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Logo (PNG/SVG for transparency)</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.logo} 
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="URL or upload/browse"
                  />
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    className="border-gray-600"
                    onClick={() => setShowMediaPicker(true)}
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                    </span>
                  </label>
                  {formData.logo && (
                    <img 
                      src={resolveMediaUrl(formData.logo)} 
                      alt="Logo preview" 
                      className="h-10 w-10 object-contain bg-gray-700 rounded p-1"
                    />
                  )}
                </div>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400 text-gray-900">Save League</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Leagues grouped by sport */}
      {sports.map(sport => {
        const sportLeagues = leagues.filter(l => l.sport_id === sport.id);
        if (sportLeagues.length === 0) return null;
        
        return (
          <div key={sport.id} className="space-y-3">
            <h3 className="text-lg font-semibold text-sky-300 flex items-center gap-2">
              <SportIcon sport={sport} className="w-5 h-5" /> {sport.name} ({sportLeagues.length})
            </h3>
            <div className="grid gap-3">
              {sportLeagues.sort((a, b) => (a.order || 0) - (b.order || 0)).map(league => (
                <Card key={league.id} className="bg-gray-800/50 border-sky-900/30">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {league.logo ? (
                        <div className="w-12 h-12 rounded-lg bg-white/10 p-1 flex items-center justify-center">
                          <img src={resolveMediaUrl(league.logo)} alt={league.name} className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-sky-300">
                          <SportIcon sport={sport} className="w-6 h-6" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-white font-semibold">{league.name}</h4>
                        <p className="text-sm text-gray-400">/{league.slug} • Order: {league.order || 0}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(league)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30" onClick={() => handleDelete(league.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {leagues.length === 0 && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No leagues yet. Add your first league to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Admin Teams
const AdminTeams = ({ teams, leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ league_ids: [], name: '', slug: '', logo: '', short_name: '' });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type (PNG or SVG for transparency)
    const validTypes = ['image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, SVG, or WebP file for transparent logos');
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', `${formData.name || 'Team'} logo`);
      uploadFormData.append('tags', 'logo,team');
      
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Set the uploaded URL as the logo
      const logoUrl = resolveMediaUrl(res.data.url);
      setFormData({ ...formData, logo: logoUrl });
    } catch (error) {
      alert('Error uploading logo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const toggleLeague = (leagueId) => {
    setFormData(prev => ({
      ...prev,
      league_ids: prev.league_ids.includes(leagueId)
        ? prev.league_ids.filter(id => id !== leagueId)
        : [...prev.league_ids, leagueId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await api.updateTeam(editingTeam.id, formData);
      } else {
        await api.createTeam(formData);
      }
      setShowForm(false);
      setEditingTeam(null);
      setFormData({ league_ids: [], name: '', slug: '', logo: '', short_name: '' });
      onRefresh();
    } catch (error) {
      alert('Error saving team');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    // Support both old league_id and new league_ids format
    const leagueIds = team.league_ids || (team.league_id ? [team.league_id] : []);
    setFormData({ ...team, league_ids: leagueIds });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        await api.deleteTeam(id);
        onRefresh();
      } catch (error) {
        alert('Error deleting team');
      }
    }
  };

  const getLeagueName = (leagueId) => leagues.find(l => l.id === leagueId)?.name || null;
  const getSportForLeague = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return sports.find(s => s.id === league?.sport_id);
  };
  
  // Get all league names for a team (supports both old and new format)
  const getTeamLeagues = (team) => {
    const ids = team.league_ids || (team.league_id ? [team.league_id] : []);
    const names = ids.map(id => getLeagueName(id)).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'No league assigned';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Teams ({teams.length})</h2>
        <Button onClick={() => { setShowForm(true); setEditingTeam(null); setFormData({ league_ids: [], name: '', slug: '', logo: '', short_name: '' }); }} className="bg-sky-300 hover:bg-sky-400">
          <Plus className="w-4 h-4 mr-2" /> Add Team
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingTeam ? 'Edit Team' : 'Add New Team'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Leagues / Competitions</Label>
                  <p className="text-gray-500 text-xs mb-2">Select all leagues this team participates in</p>
                  <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto bg-gray-900/50 p-3 rounded-lg">
                    {leagues.map(league => (
                      <label 
                        key={league.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          formData.league_ids.includes(league.id) 
                            ? 'bg-sky-600/30 border border-sky-500' 
                            : 'bg-gray-700/50 hover:bg-gray-700 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.league_ids.includes(league.id)}
                          onChange={() => toggleLeague(league.id)}
                          className="rounded border-gray-500"
                        />
                        <span className="text-white text-sm flex items-center gap-1">
                          <SportIcon sport={getSportForLeague(league.id)} className="w-3 h-3" /> {league.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.league_ids.length > 0 && (
                    <p className="text-sky-400 text-xs mt-1">{formData.league_ids.length} league(s) selected</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Team Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Team Name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Short Name</Label>
                  <Input 
                    value={formData.short_name} 
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., NYC"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Logo (PNG/SVG for transparency)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.logo} 
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                      placeholder="URL or upload/browse"
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-600"
                      onClick={() => setShowMediaPicker(true)}
                    >
                      <Image className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                        <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                      </span>
                    </label>
                    {formData.logo && (
                      <img 
                        src={resolveMediaUrl(formData.logo)} 
                        alt="Logo preview" 
                        className="h-10 w-10 object-contain bg-gray-700 rounded p-1"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400">Save Team</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {teams.map(team => (
          <Card key={team.id} className="bg-gray-800/50 border-sky-900/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {team.logo ? (
                  <div className="w-12 h-12 rounded-lg bg-white/10 p-1 flex items-center justify-center">
                    <img src={resolveMediaUrl(team.logo)} alt={team.name} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-gray-400">
                    <Users className="w-6 h-6" />
                  </div>
                )}
                <div>
                  <h3 className="text-white font-semibold">{team.name} {team.short_name && `(${team.short_name})`}</h3>
                  <p className="text-sm text-sky-200">{getTeamLeagues(team)}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(team)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(team.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {teams.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No teams yet. Add your first team!
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Games

export { AdminPartners, AdminPageBanners, AdminLeagues, AdminTeams };
